import { GiftCard } from '@repo/ui/src/types/basket';
import { Order, Package } from '@repo/ui/src/types/order';
import { ShipmentStatuses } from '@views/OrderDetail/ShipmentStatus';
import React from 'react';
import { LineItem } from '@repo/ui/src/types/lineItem';

interface Shipment extends Package {
  shipmentStatus: ShipmentStatuses;
  productItems?: Array<LineItem>;
  giftCertificateItems?: Array<GiftCard>;
}
const getShipmentStatus = (status: string) => {
  if (
    [
      'shipment_confirmation_standard',
      'delay_overdue',
      'delivery_anticipation_standard',
      'delivery_attempt_standard',
    ].includes(status)
  ) {
    return ShipmentStatuses.shipped;
  }
  if (status === 'delivered_standard') {
    return ShipmentStatuses.delivered;
  }
  return ShipmentStatuses.processing;
};

const decodeTrackingURL = (trackingUrl: string) =>
  trackingUrl.replace(/&#x([0-9abcdef]{1,4});/gi, (match, numStr) =>
    String.fromCharCode(parseInt(numStr, 16)),
  );
export const useOrderShipments = (order?: Order) => {
  return React.useMemo<Array<Shipment>>(() => {
    if (!order) {
      return [];
    }
    const knownPackages =
      order.details.packages?.map<Shipment>((orderPackage) => ({
        ...orderPackage,
        shipmentStatus: getShipmentStatus(orderPackage.status),
        productItems: orderPackage.items?.map(
          (item) =>
            ({
              ...order.details.productItems.find((product) => product.sku === item.sku),
              ...item,
            }) as LineItem,
        ),
      })) ?? [];

    const giftCardPackage = order.details.giftCertificateItems?.length
      ? ({
          giftCertificateItems: order.details.giftCertificateItems,
          shipmentStatus: ShipmentStatuses.digital,
        } as Shipment)
      : null;

    const packagedItems =
      order.details.packages
        ?.map(({ items }) => [...(items?.map(({ sku }) => sku) ?? [])])
        .flat() ?? [];
    const remainingItems =
      order.details.productItems.filter(
        (product) => !packagedItems.some((sku) => sku === product.sku),
      ) ?? [];

    const isOrderComplete = order.details.status === 'completed';

    const remainingPackage = remainingItems?.length
      ? ({
          productItems: remainingItems,
          shipmentStatus: isOrderComplete
            ? ShipmentStatuses.delivered
            : ShipmentStatuses.processing,
        } as Shipment)
      : null;
    return [giftCardPackage, remainingPackage, ...knownPackages]
      .filter<Shipment>(Boolean as any)
      .map(
        (shipment) =>
          ({
            ...shipment,
            trackingUrl: shipment.trackingUrl ? decodeTrackingURL(shipment.trackingUrl) : undefined,
          }) as Shipment,
      )
      .sort((a, b) => (a.shipmentStatus < b.shipmentStatus ? -1 : 1));
  }, [order]);
};
